/**
 * App shell — sidebar + top bar + content. Linear-density, deliberate
 * typography, refined motion. Brand-anchored at the top with the Paybyrd
 * wordmark, navigation grouped under section headings, theme + user
 * controls in the top bar.
 */

import { type ReactNode, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";

import MenuIcon from "@mui/icons-material/MenuOutlined";

import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import PeopleIcon from "@mui/icons-material/GroupsOutlined";
import VoucherIcon from "@mui/icons-material/LocalOfferOutlined";
import RedeemIcon from "@mui/icons-material/RedeemOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import LoyaltyIcon from "@mui/icons-material/LoyaltyOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import LaptopIcon from "@mui/icons-material/LaptopMacOutlined";
import LanguageIcon from "@mui/icons-material/LanguageOutlined";

import { isAuth0Configured, useAuth } from "../providers/AuthProvider";
import { useColorMode } from "../providers/ColorModeProvider";
import { useI18n, type Lang } from "../providers/I18nProvider";
import { useAccount } from "../providers/AccountProvider";
import { useCommandPalette } from "./CommandPalette";
import { tokens } from "../../lib/theme";
import { partnerPortalUrl } from "../../lib/hosts";

const DRAWER_WIDTH = 248;

interface NavItem {
  to: string;
  /** i18n key resolved at render time via `t(labelKey)`. */
  labelKey: string;
  icon: ReactNode;
  /** i18n key for the section heading, resolved at render time. */
  sectionKey: string;
}

const NAV: NavItem[] = [
  { to: "/overview",    labelKey: "nav.overview",    icon: <DashboardIcon  fontSize="small" />, sectionKey: "shell.section.dashboard" },
  { to: "/partners",    labelKey: "nav.partners",    icon: <PeopleIcon     fontSize="small" />, sectionKey: "shell.section.program" },
  { to: "/vouchers",    labelKey: "nav.vouchers",    icon: <VoucherIcon    fontSize="small" />, sectionKey: "shell.section.program" },
  { to: "/redemptions", labelKey: "nav.redemptions", icon: <RedeemIcon     fontSize="small" />, sectionKey: "shell.section.program" },
  { to: "/rules",       labelKey: "nav.rules",       icon: <LoyaltyIcon    fontSize="small" />, sectionKey: "shell.section.program" },
  { to: "/payouts",     labelKey: "nav.payouts",     icon: <PaymentsIcon   fontSize="small" />, sectionKey: "shell.section.finance" },
  { to: "/audit",       labelKey: "nav.audit",       icon: <HistoryIcon    fontSize="small" />, sectionKey: "shell.section.insights" },
];

export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile drawer whenever the route changes — feels right
  // and matches what users expect after tapping a nav item.
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [pathname, isMobile]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: "saturate(180%) blur(12px)",
        }}
      >
        <Toolbar sx={{ minHeight: 56, gap: { xs: 1, md: 2 }, px: { xs: 1.5, md: 3 } }}>
          {isMobile && (
            <IconButton
              size="small"
              edge="start"
              onClick={() => setMobileOpen(true)}
              aria-label={t("shell.openNavigation")}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}
          <Wordmark />
          <AccountSwitcher />
          <Box sx={{ flexGrow: 1 }} />
          <CommandPaletteHint />
          <LanguageSwitcher />
          <ColorModeButton />
          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* Sidebar — permanent on desktop, temporary on mobile. Same content
          rendered once via NavContents so the two variants stay in sync. */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              bgcolor: "background.paper",
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <Toolbar />
          <NavContents pathname={pathname} />
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              bgcolor: "background.paper",
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <Toolbar />
          <NavContents pathname={pathname} />
        </Drawer>
      )}

      {/* Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 7,
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          maxWidth: { xl: 1440 },
          width: "100%",
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function NavContents({ pathname }: { pathname: string }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useI18n();

  const sections = NAV.reduce<Record<string, NavItem[]>>((acc, item) => {
    (acc[item.sectionKey] ??= []).push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ overflow: "auto", py: 2.5, display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1 }}>
        {Object.entries(sections).map(([sectionKey, items]) => (
          <Box key={sectionKey} sx={{ px: 1.5, mb: 2 }}>
            <Typography variant="overline" sx={{ display: "block", px: 1.5, mb: 0.75 }}>
              {t(sectionKey)}
            </Typography>
            <List disablePadding>
              {items.map((item) => {
                const active =
                  pathname === item.to ||
                  (pathname === "/" && item.to === "/overview");
                return (
                  <ListItemButton
                    key={item.to}
                    component={Link}
                    to={item.to}
                    selected={active}
                    sx={{
                      py: 0.85,
                      px: 1.5,
                      mb: 0.25,
                      borderRadius: 2,
                      color: active ? "primary.main" : "text.secondary",
                      fontWeight: active ? 600 : 500,
                      position: "relative",
                      "&.Mui-selected": {
                        bgcolor: alpha(tokens.brand.purple, isDark ? 0.16 : 0.08),
                        "&:hover": { bgcolor: alpha(tokens.brand.purple, isDark ? 0.22 : 0.12) },
                      },
                      "&::before": active
                        ? {
                            content: '""',
                            position: "absolute",
                            left: -6,
                            top: "20%",
                            bottom: "20%",
                            width: 3,
                            borderRadius: 2,
                            bgcolor: "primary.main",
                          }
                        : {},
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                        color: active ? "primary.main" : "text.tertiary",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(item.labelKey)}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: active ? 600 : 500,
                        letterSpacing: "-0.005em",
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mx: 1.5, opacity: 0.6 }} />
      <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, color: "text.tertiary" }}>
          v0.1 · phase 1
        </Typography>
        <Typography variant="caption" sx={{ color: "text.tertiary" }}>
          {t("shell.partnerProgram")}
        </Typography>
      </Box>
    </Box>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────── */

function Wordmark() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      {/* P-mark only, gradient. Real Paybyrd brand mark from
          frontend/public/paybyrd-mark.svg. Square-ish viewBox so MUI's
          icon-style sizing works cleanly at any height. */}
      <Box
        component="img"
        src="/paybyrd-mark.svg"
        alt="Paybyrd"
        sx={{ height: 22, width: "auto", display: "block" }}
      />
      <Typography
        sx={{
          fontFamily: tokens.font.display,
          fontWeight: 600,
          fontSize: 15,
          letterSpacing: "-0.015em",
          lineHeight: 1.1,
          color: theme.palette.text.primary,
          // The mark already says "Paybyrd" — wordmark text here is the
          // product label only ("Engage").
        }}
      >
        <Box component="span" sx={{ color: isDark ? "rgba(255,255,255,0.55)" : "rgba(15,10,31,0.55)" }}>
          Paybyrd
        </Box>{" "}
        <Box component="span" sx={{ color: "primary.main" }}>Engage</Box>
      </Typography>
    </Stack>
  );
}

function CommandPaletteHint() {
  const { open } = useCommandPalette();
  const { t } = useI18n();
  const theme = useTheme();
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <Tooltip title={t("shell.commandPalette.tooltip")}>
      <Box
        component="button"
        onClick={open}
        aria-label={t("shell.commandPalette.aria")}
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.text.primary, 0.025),
          color: "text.tertiary",
          fontFamily: tokens.font.body,
          fontSize: 12.5,
          fontWeight: 450,
          letterSpacing: "-0.005em",
          cursor: "pointer",
          transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderColor: alpha(theme.palette.primary.main, 0.4),
            color: "text.primary",
          },
        }}
      >
        <span>{t("common.search")}</span>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 28,
            height: 18,
            px: 0.5,
            borderRadius: 0.75,
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.12 : 0.07),
            fontFamily: tokens.font.mono,
            fontSize: 11,
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          {isMac ? "⌘K" : "Ctrl K"}
        </Box>
      </Box>
    </Tooltip>
  );
}

function AccountSwitcher() {
  const { accountId, accounts, setAccountId } = useAccount();
  const { t } = useI18n();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  // Hide entirely when there's only one account — no point cluttering
  // the toolbar with a one-item dropdown.
  if (accounts.length <= 1) return null;

  const active = accounts.find((a) => a.id === accountId);
  const label = active?.display_name ?? accountId;

  return (
    <>
      <Box
        component="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t("shell.switchAccount")}
        sx={{
          ml: 1,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.text.primary, 0.025),
          color: "text.secondary",
          fontFamily: tokens.font.body,
          fontSize: 12.5,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          cursor: "pointer",
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderColor: alpha(theme.palette.primary.main, 0.4),
            color: "text.primary",
          },
        }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </Box>
        <Box component="span" sx={{ color: "text.tertiary", fontSize: 10 }}>▾</Box>
      </Box>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {accounts.map((a) => (
          <MenuItem
            key={a.id}
            selected={a.id === accountId}
            onClick={() => {
              setAccountId(a.id);
              setAnchor(null);
            }}
            sx={{ minWidth: 240, fontSize: 13.5 }}
          >
            <Box>
              <Box sx={{ fontWeight: 500 }}>{a.display_name}</Box>
              <Box sx={{ fontSize: 11, color: "text.tertiary", fontFamily: tokens.font.mono }}>
                {a.id} · {a.partner_count} partner{a.partner_count === 1 ? "" : "s"}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const LANG_LABELS: Record<Lang, string> = { en: "English", pt: "Português", nl: "Nederlands" };
const LANG_CODES: Record<Lang, string> = { en: "EN", pt: "PT", nl: "NL" };

function LanguageSwitcher() {
  const { lang, setLang, t } = useI18n();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title={`${t("shell.language")} · ${LANG_LABELS[lang]}`}>
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={t("shell.changeLanguage")}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <LanguageIcon fontSize="small" />
          <Box
            component="span"
            sx={{
              fontFamily: tokens.font.mono,
              fontSize: 11,
              fontWeight: 500,
              color: "text.secondary",
              ml: 0.25,
            }}
          >
            {LANG_CODES[lang]}
          </Box>
        </IconButton>
      </Tooltip>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
          <MenuItem
            key={l}
            selected={l === lang}
            onClick={() => {
              setLang(l);
              setAnchor(null);
            }}
            sx={{ fontSize: 13.5, minWidth: 160 }}
          >
            <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11, color: "text.tertiary", width: 28 }}>
              {LANG_CODES[l]}
            </Box>
            {LANG_LABELS[l]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ColorModeButton() {
  const { preference, toggle } = useColorMode();
  const { t } = useI18n();
  const Icon = preference === "light" ? LightModeIcon : preference === "dark" ? DarkModeIcon : LaptopIcon;
  const label =
    preference === "system"
      ? t("shell.theme.system")
      : preference === "dark"
        ? t("shell.theme.dark")
        : t("shell.theme.light");
  return (
    <Tooltip title={`${label} ${t("shell.theme.cycleHint")}`}>
      <IconButton size="small" onClick={toggle} aria-label={t("shell.theme.cycleAria")}>
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [isAlsoPartner, setIsAlsoPartner] = useState(false);

  // Probe /portal/me on mount — if 200, the logged-in admin is also
  // registered as a partner and gets a cross-link in the menu.
  useEffect(() => {
    let cancelled = false;
    import("../../lib/api").then(({ api }) => {
      api
        .get("/portal/me")
        .then(() => !cancelled && setIsAlsoPartner(true))
        .catch(() => !cancelled && setIsAlsoPartner(false));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isAuth0Configured()) {
    return (
      <Typography variant="caption" sx={{ color: "text.tertiary", fontFamily: "monospace" }}>
        {t("shell.devKeyMode")}
      </Typography>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {user?.email && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 450,
              display: { xs: "none", md: "inline" },
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            {user.email}
          </Typography>
        )}
        <Button size="small" variant="outlined" onClick={(e) => setAnchor(e.currentTarget)}>
          {t("shell.account")}
        </Button>
      </Stack>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1.5, minWidth: 240 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{user?.email}</Typography>
          <Typography variant="caption" color="text.secondary">{t("shell.adminView")}</Typography>
        </Box>
        {isAlsoPartner && (
          <MenuItem
            component="a"
            href={partnerPortalUrl("/dashboard")}
            onClick={() => setAnchor(null)}
            sx={{ fontSize: 13.5 }}
          >
            {t("shell.openPartnerPortal")} →
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout();
          }}
          sx={{ fontSize: 13.5 }}
        >
          {t("shell.signOut")}
        </MenuItem>
      </Menu>
    </>
  );
}
